import { useState, useEffect } from 'react';

import { getTrans } from '@/utils/instantLoaded/tools';
import { handleClickHistoryButton, formatPrice } from '@/utils/tools';
import API from '@/services';
import { cdnFile } from '@/utils/cdnFile';
import { assignParams } from '@/utils/dataTypeConvert';
import {
  INPUT_TYPE,
  TOTAL_PAYMENT_TEXT,
  REFUND_AMOUNT_TEXT,
  ITEMS_TEXT,
  ITEM_TEXT,
  BUYER_ORDER_LIST_TITLE_TEXT,
  SELLER_ORDER_LIST_TITLE_TEXT,
  TRACKING_SECTION,
  TRACKING_TYPE,
  TRACKING_TARGET,
  TRACKING_MSG_TYPE,
  ReportSelectButtonId,
  ReportSelectButtonType,
  ReportSelectMoreButtonId,
  ReportSelectMoreButtonType,
  ReportListButtonId,
  ReportListButtonType,
  TFEOrderUserType,
  TOTAL_TEXT,
  SELECT_RR_REQUEST_TEXT,
  SELECT_ORDER_TEXT,
  SELECT_RR_TEXT,
  MORE_RR_REQUEST_TEXT,
  MORE_ORDER_TEXT,
  TO_PAY_TEXT,
  TO_SHIP_TEXT,
  TO_RECEIVE_TEXT,
  COMPLETED_TEXT,
  CANCELLED_TEXT,
  UNPAID_TEXT,
  SHIPPING_TEXT,
  ALL_TEXT,
  SELLER_CANCELLED_TEXT,
  RETURN_REFUND_TEXT,
  SELLER_RETURN_REFUND_TEXT,
  VIEW_ALL_TEXT,
  VIEW_ALL_ORDERS_TEXT,
  SELECT_TEXT,
  ORDER_SHOW_TYPE,
  ChatbotComponentType,
  OrderSendScene,
  OrderType,
  OrderSelectionRanking,
  RuleBasedMatchOptionTriggeredType,
} from '@/utils/constants';
import { OrderCardType } from '@shopee/chatbot-ui/lib/components/OrderList';
import { USER_SEND_TYPE } from '@/types/history';
import { handleOrderRedirect } from '@/utils/btnClickHandler';
import { orderListTypeToText } from '@/components/ProductList/ProductList';
import { handleRequest } from '@/utils';
import { initStore } from '@/stores';
import { DetectUserInteractionInstance } from '@/components/DetectUserInteraction';
import { REPLY_TYPE } from '@/utils/responseHandler';

interface loadMoreParams {
  [name: string]: number;
  page_num: number;
  page_size: number;
  status: number;
}

export const orderListPageSectionDataMap = {
  [OrderCardType.oldCarousel]: TRACKING_SECTION[TRACKING_TYPE.OLD_CAROUSEL],
  [OrderCardType.newCarousel]: TRACKING_SECTION[TRACKING_TYPE.NEW_CAROUSEL_ORDER_CARD],
  [OrderCardType.bigCarousel]: TRACKING_SECTION[TRACKING_TYPE.BIG_CAROUSEL_ORDER_CARD],
  [OrderCardType.verticalList]: TRACKING_SECTION[TRACKING_TYPE.VERTICAL_LIST_ORDER_CARD],
  [OrderCardType.twoRowCarousel]: TRACKING_SECTION[TRACKING_TYPE.TWO_ROW_CAROUSEL_ORDER_CARD],
  [OrderCardType.orderWithCopy]: '',
  [OrderCardType.smallOrder]: '',
  [OrderCardType.foodCard]: '',
  [OrderCardType.foodCardInList]: '',
  [OrderCardType.expeditedDelivery]: '',
};

export const returnRefundType = -1;
export const returnRefundTFEType = 11; // 在 web form 里 这里需要 11

const typeAll = 0;

const buyerQueryType = {
  toPay: 1,
  toShip: 2,
  toReceive: 3,
  completed: 4,
  cancelled: 5,
};

const sellerQueryType = {
  unPaid: 6,
  toShip: 7,
  shipped: 8,
  completed: 9,
  cancelled: 10,
};

export const DEFAULT_BUYER_TITLES = [
  { name: getTrans(ALL_TEXT), type: typeAll },
  { name: getTrans(TO_PAY_TEXT), type: buyerQueryType.toPay },
  { name: getTrans(TO_SHIP_TEXT), type: buyerQueryType.toShip },
  { name: getTrans(TO_RECEIVE_TEXT), type: buyerQueryType.toReceive },
  { name: getTrans(COMPLETED_TEXT), type: buyerQueryType.completed },
  { name: getTrans(CANCELLED_TEXT), type: buyerQueryType.cancelled },
];

export const All_BUYER_TITLES = [
  { name: getTrans(ALL_TEXT), type: typeAll },
  { name: getTrans(TO_PAY_TEXT), type: buyerQueryType.toPay },
  { name: getTrans(TO_SHIP_TEXT), type: buyerQueryType.toShip },
  { name: getTrans(TO_RECEIVE_TEXT), type: buyerQueryType.toReceive },
  { name: getTrans(COMPLETED_TEXT), type: buyerQueryType.completed },
  { name: getTrans(CANCELLED_TEXT), type: buyerQueryType.cancelled },
  { name: getTrans(RETURN_REFUND_TEXT), type: returnRefundType },
];

export const All_TFE_BUYER_TITLES = [
  // 这个跟上面的区别就是 return refund type 的区别
  { name: getTrans(ALL_TEXT), type: typeAll },
  { name: getTrans(TO_PAY_TEXT), type: buyerQueryType.toPay },
  { name: getTrans(TO_SHIP_TEXT), type: buyerQueryType.toShip },
  { name: getTrans(TO_RECEIVE_TEXT), type: buyerQueryType.toReceive },
  { name: getTrans(COMPLETED_TEXT), type: buyerQueryType.completed },
  { name: getTrans(CANCELLED_TEXT), type: buyerQueryType.cancelled },
  { name: getTrans(RETURN_REFUND_TEXT), type: returnRefundTFEType },
];

const DEFAULT_SELLER_TITLES = [
  { name: getTrans(ALL_TEXT), type: typeAll },
  { name: getTrans(UNPAID_TEXT), type: sellerQueryType.unPaid },
  { name: getTrans(TO_SHIP_TEXT), type: sellerQueryType.toShip },
  { name: getTrans(SHIPPING_TEXT), type: sellerQueryType.shipped },
  { name: getTrans(COMPLETED_TEXT), type: sellerQueryType.completed },
  { name: getTrans(SELLER_CANCELLED_TEXT), type: sellerQueryType.cancelled },
];

export const DEFAULT_ORDER_TYPE_DECS = {
  [buyerQueryType.toPay]: getTrans(TO_PAY_TEXT),
  [buyerQueryType.toShip]: getTrans(TO_SHIP_TEXT),
  [buyerQueryType.toReceive]: getTrans(TO_RECEIVE_TEXT),
  [buyerQueryType.completed]: getTrans(COMPLETED_TEXT),
  [buyerQueryType.cancelled]: getTrans(CANCELLED_TEXT),
  [sellerQueryType.unPaid]: getTrans(UNPAID_TEXT),
  [sellerQueryType.toShip]: getTrans(TO_SHIP_TEXT),
  [sellerQueryType.shipped]: getTrans(SHIPPING_TEXT),
  [sellerQueryType.completed]: getTrans(COMPLETED_TEXT),
  [sellerQueryType.cancelled]: getTrans(SELLER_CANCELLED_TEXT),
};

const NODE_MAP = {
  CHECK: 'RR_Check',
  RAISE: 'RR_Raise',
  ORDER: 'Order',
};

const SEND_SCENE_MAP = {
  [OrderType.prediction]: OrderSendScene.LandingPage,
  [OrderType.shortcutOrder]: OrderSendScene.ShortcutOrder,
};

export const useForbidOrderRedirect = (isSeller?: boolean) => {
  const forbidOrderRedirectCountries = ['co', 'cl'];
  if (isSeller && forbidOrderRedirectCountries.includes(COUNTRY)) {
    return true;
  }
  return false;
};

export const formatOrder = (
  order: any,
  info?: {
    status?: number;
    isRROrder?: boolean;
    isSeller?: boolean;
    forbidOrderRedirect?: boolean;
  },
) => {
  const {
    status,
    isRROrder = order.return_id || order.refund_id ? true : false,
    isSeller,
    forbidOrderRedirect,
  } = info || {};
  const item = order.items?.[0] || {};
  const orderImg = item?.image?.[0] || '';
  const shopImg = order.shop_image?.[0] || '';
  const orderStatusText
    = status === 0
      ? orderListTypeToText[order.order_list_type as keyof typeof orderListTypeToText]
      : orderListTypeToText[status as keyof typeof orderListTypeToText];

  return {
    title: item.title,
    specification: item.specification,
    orderId: order.order_id,
    orderSn: order.order_sn,
    returnSn: order.return_sn,
    returnId: order.return_id,
    orderStatusText: isRROrder ? order.return_status : getTrans(orderStatusText),
    totalPaymentText: isRROrder ? getTrans(REFUND_AMOUNT_TEXT) : getTrans(TOTAL_PAYMENT_TEXT),
    amount: item.amount,
    orderPrice: formatPrice(item.order_price),
    totalPrice: isRROrder ? formatPrice(order.refund_amount) : formatPrice(order.total_price),
    itemCount: order.item_count,
    itemsText: getTrans(ITEMS_TEXT),
    itemText: getTrans(ITEM_TEXT),
    image: cdnFile(orderImg),
    totalText: getTrans(TOTAL_TEXT),
    price: isRROrder ? formatPrice(order.refund_amount) : formatPrice(order.total_price),
    shopName: order.shop_name,
    shopImg: cdnFile(shopImg) || undefined,
    showOrderId: isSeller,
    handleOrderDetailRedirect: forbidOrderRedirect
      ? undefined
      : () =>
        handleOrderRedirect({
          uri_key: order.uri_key,
          order_id: order.order_id,
          return_id: order.return_id,
          checkout_id: order.checkout_id,
          shop_id: order.shop_id,
        }),
  };
};

export const oldTaskbotLoadMore = async (
  loadMoreProps: {
    page_num: number;
    page_size: number;
    status: number;
  },
  sessionInfo: {
    is_seller: boolean;
    sessionId: string;
    dialogueId?: string;
    node_id?: string;
  },
  forbidRedirect = false,
  { orderType = OrderType.shopee, orderCardType = OrderCardType.oldCarousel } = {},
  orderSelectionScene?: string,
) => {
  const { is_seller: isSeller, sessionId, dialogueId, node_id } = sessionInfo;
  const { page_num, page_size, status } = loadMoreProps;
  const isRROrder = status === returnRefundType;
  const nextParams = {
    session_id: sessionId,
    dialogue_id: dialogueId,
    input_type: INPUT_TYPE.BUTTON,
  };
  const forbidOrderRedirect = forbidRedirect ?? useForbidOrderRedirect(isSeller);

  const res = isRROrder
    ? await (API as any)
      .getReturnList({
        page_num,
        page_size,
        is_seller: isSeller ? 1 : 0,
      })
      .catch(() => {})
    : await (API as any)
      .getOrderList({
        query_type: status,
        page_num,
        page_size,
        is_seller: isSeller ? 1 : 0, // this.props.userStore.isSeller
      })
      .catch(() => {});

  const order_list = (isRROrder ? (res || {}).return_list : (res || {}).order_list) || [];

  const orderList = order_list.map((order: any) => {
    const orderItem = formatOrder(order, { status, isRROrder, isSeller, forbidOrderRedirect });
    const item = order.items?.[0] || {};

    const params = {
      return_id: order?.return_id,
      order_id: order?.order_id,
      user_send: {
        type: USER_SEND_TYPE.ORDER_SELECT,
        send_scene: orderSelectionScene ?? SEND_SCENE_MAP[orderType as keyof typeof SEND_SCENE_MAP],
        order_select: {
          ...order, // TDDO: 是否需要增加
          price: isRROrder ? order.refund_amount : order.total_price,
          image:
            orderType === OrderType.prediction || orderType === OrderType.shortcutOrder
              ? cdnFile(item.image[0])
              : item.image[0] || '',
          item_amount: item.amount | 1,
          name: item.title,
          shouldRedirect: !forbidOrderRedirect,
          order_type: orderType,
          status_text: orderItem.orderStatusText,
          id: order?.order_id,
        },
      },
    };

    return {
      ...orderItem,
      action: {
        info: {
          ...orderItem,
          orderCardType,
          // price: formatPrice(item.order_price),
          handleClick: forbidOrderRedirect
            ? undefined
            : () =>
              handleOrderRedirect({
                uri_key: order.uri_key,
                order_id: order.order_id,
                return_id: order.return_id,
                checkout_id: order.checkout_id,
                shop_id: order.shop_id,
              }),
        },
        func: 'showOrder',
        nextParams: {
          ...nextParams,
          message_text: item.title,
          message_extra: {
            node_id,
            params: params || {},
          },
        },
        orderType,
      },
    };
  });
  return { data: orderList };
};

export const UseProps = (
  type: ChatbotComponentType,
  userInteractionInstance: DetectUserInteractionInstance,
  props: any,
) => {
  const [isFirstClose, setIsFirstClose] = useState(true);
  const [defaultSelected, setDefaultSelected] = useState(0);
  const [buyerTitles, setBuyerTitles] = useState(DEFAULT_BUYER_TITLES);
  const [sellerTitles, setSellerTitles] = useState(DEFAULT_SELLER_TITLES);
  const { customCallback = {}} = props;

  const startDetectInteraction = () => {
    userInteractionInstance.current?.start?.();
  };

  const stopDetectInteraction = () => {
    userInteractionInstance.current?.stop?.();
  };

  const clickHandlerProps = {
    onButtonClick: stopDetectInteraction,
    onOrderClick: stopDetectInteraction,
    onShowMoreButtonClick: stopDetectInteraction,
  };

  if (type === ChatbotComponentType.ChatbotUI) {
    const {
      selectOtherAction,
      selectAction,
      tabs,
      defaultStatus,
      userType,
      statusText,
      roundId,
      dialogueId,
      threadNodeId,
      threadId,
      closeOtherAction,
      taskbotInfo,
      orderCardType = OrderCardType.oldCarousel,
      isShortcutOrder,
    } = props;

    const isSeller = userType === TFEOrderUserType.Seller;
    const nextParams = {
      session_id: props.sessionId,
      dialogue_id: props.dialogueId,
      thread_id: props.threadId,
      thread_id_v2: props.threadIdV2,
      round_id: props.roundId,
      input_type: INPUT_TYPE.BUTTON,
      thread_node_id: props.threadNodeId,
    };

    const onLoadMore = async (loadMoreProps: loadMoreParams) => {
      const params = selectOtherAction?.params || {};

      const res = await (API as { postMessage: Function })
        .postMessage(
          {
            ...nextParams,
            is_bypass: props.selectOtherAction.is_bypass,
            message_extra: { task_bot_action: { ...selectOtherAction, ...{ params: assignParams(params, loadMoreProps) }}},
            ...customCallback,
          },
          { isErrorWhiteList: true },
        )
        .catch(() => {
          throw new Error();
        });

      const order_list = res?.replies[0]?.message_extra?.task_flow_card?.order_list || [];
      const statusText = res?.replies[0]?.message_extra?.task_flow_card?.status_text || {};
      const orderTaskbotInfo = res?.replies[0]?.message_extra?.task_flow_card?.task_bot_info || {};
      const orderList = order_list.map((order: any) => {
        const isRROrder = order.return_id || order.refund_id ? true : false;
        const item = order.items?.[0] || {};
        const orderImg = item?.image[0] || '';

        const orderItem = {
          title: item.title,
          specification: item.specification,
          orderId: order.order_id,
          orderSn: order.order_sn,
          returnSn: order.return_sn,
          returnId: order.return_id,
          orderStatusText: isRROrder ? order.return_status : statusText[order.order_list_type],
          totalPaymentText: isRROrder ? getTrans(REFUND_AMOUNT_TEXT) : getTrans(TOTAL_PAYMENT_TEXT),
          amount: item.amount,
          orderPrice: formatPrice(item.order_price),
          totalPrice: isRROrder ? formatPrice(order.refund_amount) : formatPrice(order.total_price),
          price: isRROrder ? formatPrice(order.refund_amount) : formatPrice(order.total_price),
          itemCount: order.item_count,
          image: cdnFile(orderImg),
          shopName: order.shop_name,
          // 这里加下面这行是为了 让 view more 和 外面列表的点击保持一致 看起来之前的开发同学加的时候 没有考虑到 view more 的情况
          orderCardType:
            orderCardType === OrderCardType.oldCarousel ? OrderCardType.oldCarousel : OrderCardType.newCarousel,
          handleOrderDetailRedirect: useForbidOrderRedirect(isSeller)
            ? undefined
            : () =>
              handleOrderRedirect({
                uri_key: order.uri_key,
                order_id: order.order_id,
                return_id: order.return_id,
                checkout_id: order.checkout_id,
                shop_id: order.shop_id,
              }),
        };

        return {
          ...orderItem,
          action: {
            info: {
              ...orderItem,
              handleClick: useForbidOrderRedirect(isSeller)
                ? undefined
                : () =>
                  handleOrderRedirect({
                    uri_key: order.uri_key,
                    order_id: order.order_id,
                    return_id: order.return_id,
                    checkout_id: order.checkout_id,
                    shop_id: order.shop_id,
                  }),
            },
            func: 'showOrder',
            nextParams: {
              ...nextParams,
              is_bypass: props.selectAction.is_bypass,
              message_text: item.title,
              message_extra: {
                task_bot_action: {
                  ...selectAction,
                  ...{
                    params: assignParams(props.selectAction.params, {
                      order_id: order.order_id,
                      return_id_interact: order.return_id,
                    }),
                  },
                  task_bot_info: orderTaskbotInfo,
                },
                params: {
                  // history message
                  user_send: {
                    type: USER_SEND_TYPE.ORDER_SELECT,
                    order_select: {
                      ...orderItem,
                      price: isRROrder ? order.refund_amount : order.total_price,
                      name: item.title,
                      id: item.order_id,
                      shouldRedirect: useForbidOrderRedirect(isSeller),
                    },
                  },
                },
              },
            },
          },
        };
      });
      return { data: orderList };
    };

    const content = props.content?.map((item: any, index: number) => {
      const isRROrder = item.return_id || item.refund_id ? true : false;
      const firstItemOfOrder = item.items?.[0] || {};
      const orderImg = firstItemOfOrder?.image?.[0] || '';
      return {
        ...item,
        image: cdnFile(orderImg),
        price: formatPrice(item.price),
        orderId: item.order_id,
        itemCount: item.item_count,
        orderStatusText: isRROrder ? item.return_status : statusText[item.order_list_type],
        totalPrice: isRROrder ? formatPrice(item.refund_amount) : formatPrice(item.total_price),
        showPredictedText: item.style === OrderSelectionRanking.TopTwoOrders,
        predictedText: getTrans('order_picks_for_you'),
        action: {
          info: {
            ...item,
            image: cdnFile(item.image),
            price: formatPrice(item.price),
            itemsText: getTrans(ITEMS_TEXT),
            itemText: getTrans(ITEM_TEXT),
            totalText: getTrans(TOTAL_TEXT),
            totalPrice: isRROrder ? formatPrice(item.refund_amount) : formatPrice(item.total_price),
            orderStatusText: isRROrder ? item.return_status : statusText[item.order_list_type],
            orderId: isRROrder ? item.return_id : item.order_id,
            orderSn: isRROrder ? item.refund_id : item.order_sn,
            returnSn: item.return_sn,
            returnId: item.return_id,
            itemCount: item.item_count,
            // 回显的Order只有新旧两种样式
            orderCardType:
              orderCardType === OrderCardType.oldCarousel ? OrderCardType.oldCarousel : OrderCardType.newCarousel,
            handleClick: useForbidOrderRedirect(isSeller) ? undefined : () => handleOrderRedirect(item),
          },
          func: 'showOrder',
          nextParams: {
            ...nextParams,
            is_bypass: props.selectAction.is_bypass,
            message_text: item.title,
            message_extra: {
              task_bot_action: {
                ...selectAction,
                ...{
                  params: assignParams(props.selectAction.params, {
                    order_id: item.order_id,
                    return_id_interact: item.return_id,
                  }),
                },
                task_bot_info: props.taskbotInfo,
              },
              params: {
                user_send: {
                  type: USER_SEND_TYPE.ORDER_SELECT,
                  order_select: {
                    ...item,
                    price: isRROrder ? item.refund_amount : item.total_price,
                    image: cdnFile(orderImg),
                    name: item.title,
                    shouldRedirect: useForbidOrderRedirect(isSeller),
                    id: item?.order_id,
                  },
                },
              },
            },
            triggered_option: {
              triggered_type: RuleBasedMatchOptionTriggeredType.OrderOrTransactionType,
              triggered_index: index,
              round_id: roundId,
              dialogue_id: dialogueId,
              reply_type: REPLY_TYPE.TFE_CARD_SELECTION,
            },
          },
        },
      };
    });

    const trackingProps = {
      componentTrackingData: {
        order: {
          trackingsection:
            orderCardType === OrderCardType.oldCarousel
              ? // @ts-ignore
              TRACKING_SECTION[TRACKING_TYPE.ORDER]
              : orderListPageSectionDataMap[orderCardType as OrderCardType],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER],
          trackingdataBasic: {
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            button_id: ReportSelectButtonId,
            button_type: ReportSelectButtonType,
            button_text: props.selectOrderText,
            task_bot_name: props.taskbotName,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          },
          trackingItemData: (data: any) => ({
            location: data.index,
            orderid: data?.order?.order_id,
            order_status: data?.order?.orderStatusText,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          }),
        },
        moreOrder: {
          trackingsection:
            orderCardType === OrderCardType.oldCarousel
              ? // @ts-ignore
              TRACKING_SECTION[TRACKING_TYPE.OTHERORDER]
              : orderListPageSectionDataMap[orderCardType as OrderCardType],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.OTHERORDER],
          trackingdataBasic: {
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            button_id: ReportSelectMoreButtonId,
            button_type: ReportSelectMoreButtonType,
            button_text: props.loadMoreOrderText,
            task_bot_name: props.taskbotName,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          },
        },
        panelOrder: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.PANELORDER],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.PANELORDER],
          trackingdataBasic: {
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
            button_id: ReportListButtonId,
            button_type: ReportListButtonType,
            button_text: props.selectOrderText,
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            task_bot_name: props.taskbotName,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            origin: 'List',
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
          },
          trackingItemData: (data: any) => ({
            order_status_tab: data?.orderStatusText,
            orderid: data?.orderId,
            order_status: data?.orderStatusText,
          }),
        },
        panelOrderButton: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_PANEL_CARD_BUTTON],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_PANEL_CARD_BUTTON],
          trackingdataBasic: {
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            origin: 'List',
            button_name: props.selectOrderText,
            button_type: ReportListButtonType,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
          },
          trackingItemData: (data: any) => ({ order_status_tab: data?.orderStatusText }),
        },
        orderList: {
          // @ts-ignore
          trackingsection: orderListPageSectionDataMap[orderCardType as OrderCardType],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL],
          trackingdataBasic: {
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
            task_bot_name: props.taskbotName,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          },
        },
        closePanel: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
          trackingdataBasic: {
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
            task_bot_name: props.taskbotName,
          },
        },
        panelOrderScroll: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
          trackingdataBasic: {
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
          },
          trackingItemData: (data: any) => ({ tab_name: data?.name }),
        },
        tab: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
          trackingdataBasic: {
            chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
            chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
            taskbot_id: props.taskbotInfo?.task_bot_id,
            taskflow_id: props.taskbotInfo?.flow_id,
            node_point_id: props.nodePointId,
            kb_bot_id: props.kbBotId,
            if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            tf_node_id: props.tfNodeId,
            tf_case_id: props.tfCaseId,
          },
          trackingItemData: (data: any) => ({ tab_name: data?.title?.name }),
        },
      },
    };

    const onClosePopup = () => {
      if (closeOtherAction && isFirstClose) {
        setIsFirstClose(false);
        handleRequest('postMessage', {
          session_id: initStore?.sessionId,
          dialogue_id: dialogueId,
          input_type: INPUT_TYPE.BUTTON,
          message_text: '',
          thread_id: threadId,
          thread_node_id: threadNodeId,
          thread_id_v2: props.threadIdV2,
          round_id: props.roundId,
          is_bypass: closeOtherAction?.is_bypass,
          message_extra: {
            task_bot_action: closeOtherAction,
            params: { order_id: 0 },
          },
          ...customCallback,
        });
      }
      startDetectInteraction();
    };

    return {
      ...trackingProps,
      ...props,
      content,
      itemsText: getTrans(ITEMS_TEXT),
      itemText: getTrans(ITEM_TEXT),
      totalText: getTrans(TOTAL_TEXT),
      showOrderId: isSeller ? true : false,
      defaultSelected: tabs?.findIndex((item: any) => item.type === defaultStatus),
      panelTitleText: isSeller ? getTrans(SELLER_ORDER_LIST_TITLE_TEXT) : getTrans(BUYER_ORDER_LIST_TITLE_TEXT),
      onLoadMore,
      onClosePopup,
      ...clickHandlerProps,
    };
  } else {
    const {
      dialogueId,
      isForRR,
      nodePointId,
      moreButtonId,
      moreButtonType,
      taskbotName,
      isHistory,
      historyDialogueId,
      historySessionId,
      orderCardType = OrderCardType.oldCarousel,
      order_list,
      node_id,
      sessionId = '',
      is_seller: isSeller,
      moreButtonText,
      selectOrderButtonText,
      isShortcutOrder,
      orderSelectionScene,
      useUIOptimisationHotQuestion,
      roundId,
    } = props;

    const trackingProps = {
      componentTrackingData: {
        order: {
          trackingsection:
            orderCardType === OrderCardType.oldCarousel
              ? // @ts-ignore
              TRACKING_SECTION[TRACKING_TYPE.ORDER]
              : orderListPageSectionDataMap[orderCardType as OrderCardType],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER],
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : sessionId,
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            node_point_id: nodePointId,
            button_text: getTrans(isForRR ? SELECT_RR_REQUEST_TEXT : SELECT_ORDER_TEXT),
            task_bot_name: taskbotName,
            if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          },
          trackingItemData: (data: any) => ({
            location: data.index,
            button_id: data?.order?.button_id,
            button_type: data?.order?.button_type,
            orderid: data?.order?.order_id,
            order_status: data?.order?.orderStatusText,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          }),
        },
        moreOrder: {
          trackingsection:
            orderCardType === OrderCardType.oldCarousel
              ? // @ts-ignore
              TRACKING_SECTION[TRACKING_TYPE.OTHERORDER]
              : orderListPageSectionDataMap[orderCardType as OrderCardType],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.OTHERORDER],
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : sessionId,
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            node_point_id: nodePointId,
            button_id: moreButtonId,
            button_type: moreButtonType,
            button_text: getTrans(isForRR ? MORE_RR_REQUEST_TEXT : MORE_ORDER_TEXT),
            task_bot_name: taskbotName,
            if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          },
        },
        orderList: {
          // @ts-ignore
          trackingsection: orderListPageSectionDataMap[orderCardType as OrderCardType],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL],
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : sessionId,
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            node_point_id: nodePointId,
            task_bot_name: taskbotName,
            if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            is_order_shortcut: isShortcutOrder ? 1 : 0,
          },
        },
        panelOrder: {
          trackingsection: TRACKING_SECTION.panelOrder,
          trackingtarget: TRACKING_TARGET.panelOrder,
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : sessionId,
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            node_point_id: nodePointId,
            // button_type: order.button_type,
            // button_id: order.button_id,
            origin: 'List',
            button_name: getTrans(isForRR ? SELECT_RR_TEXT : SELECT_ORDER_TEXT) || '',
            button_text: getTrans(isForRR ? SELECT_RR_TEXT : SELECT_ORDER_TEXT),
            task_bot_name: taskbotName,
            if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
          },
          trackingItemData: (data: any) => ({
            order_status_tab: data?.orderStatusText,
            orderid: data?.orderId,
            order_status: data?.orderStatusText,
          }),
        },
        panelOrderButton: {
          trackingsection: TRACKING_SECTION.orderSelection,
          trackingtarget: TRACKING_TARGET.button,
          trackingdataBasic: {
            chat_session_id: sessionId || '0',
            chat_dialogue_id: dialogueId || '',
            origin: 'List',
            button_name: getTrans(isForRR ? SELECT_RR_TEXT : SELECT_ORDER_TEXT),
          },
          trackingItemData: (data: any) => ({ order_status_tab: data?.orderStatusText }),
        },
        closePanel: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : sessionId,
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            node_point_id: nodePointId,
            if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            task_bot_name: taskbotName,
          },
        },
        panelOrderScroll: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : sessionId,
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            node_point_id: nodePointId,
            if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            task_bot_name: taskbotName,
          },
          trackingItemData: (data: any) => ({ tab_name: data?.name }),
        },
        tab: {
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
          trackingdataBasic: {
            chat_session_id: isHistory ? historySessionId : sessionId,
            chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
            node_point_id: nodePointId,
            if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
            task_bot_name: taskbotName,
          },
          trackingItemData: (data: any) => ({ tab_name: data?.title?.name }),
        },
      },
    };

    const initTabsAndDefaultSelect = () => {
      switch (props.show_type) {
        case ORDER_SHOW_TYPE.ORDER_BOT:
          setDefaultSelected(0); // All
          if (!props.isForRR) {
            setBuyerTitles((preVal) => [...preVal, { name: getTrans(RETURN_REFUND_TEXT), type: returnRefundType }]);
          }
          break;
        case ORDER_SHOW_TYPE.RR_BOT:
          setDefaultSelected(2); // To receive
          break;
      }

      if (props.isForRR) {
        setBuyerTitles((preVal) => [...preVal, { name: getTrans(RETURN_REFUND_TEXT), type: returnRefundType }]);

        // 新节点下的checkrr 和 老节点的chekrr 的tab disabled
        if (props.dialogueType === NODE_MAP.CHECK) {
          setSellerTitles((preVal) =>
            preVal.map((title) => ({
              disabled: true,
              ...title,
            })),
          );
        }

        setSellerTitles((preVal) => [...preVal, { name: getTrans(SELLER_RETURN_REFUND_TEXT), type: returnRefundType }]);
      }
    };

    useEffect(() => {
      initTabsAndDefaultSelect();
    }, [props.show_type, props.isForRR, props.dialogueType]);

    useEffect(() => {
      if (props.isForRR) {
        setDefaultSelected(buyerTitles.length - 1);
      }
    }, [buyerTitles, props.isForRR]);

    const onLoadMore = (loadMoreProps: any) => {
      const { is_seller, sessionId, dialogueId, node_id } = props;

      return oldTaskbotLoadMore(
        loadMoreProps,
        { is_seller, sessionId, dialogueId, node_id },
        useForbidOrderRedirect(is_seller),
        {
          orderType: isShortcutOrder ? OrderType.shortcutOrder : OrderType.shopee,
          orderCardType: isShortcutOrder
            ? useUIOptimisationHotQuestion
              ? OrderCardType.newCarousel
              : OrderCardType.smallOrder
            : OrderCardType.oldCarousel,
        },
        orderSelectionScene,
      );
    };

    const nextParams = {
      session_id: sessionId,
      dialogue_id: dialogueId,
      input_type: INPUT_TYPE.BUTTON,
    };

    const content = order_list?.map((item: any, index: number) => {
      const params = {
        return_id: item?.return_id,
        order_id: item?.order_id,
        user_send: {
          type: USER_SEND_TYPE.ORDER_SELECT,
          send_scene: orderSelectionScene
            ? orderSelectionScene
            : isShortcutOrder
              ? OrderSendScene.ShortcutOrder
              : undefined,
          order_select: {
            ...item,
            name: item.title,
            shouldRedirect: !useForbidOrderRedirect(isSeller),
            id: item?.order_id,
            order_type: isShortcutOrder ? OrderType.shortcutOrder : undefined,
          },
        },
      };

      const showPredictedLabel = (() => {
        // 如果是这三种类型 然后又是 ds 预测 需要显示绿色 label picks for you
        if (
          orderCardType === OrderCardType.newCarousel
          || orderCardType === OrderCardType.verticalList
          || orderCardType === OrderCardType.twoRowCarousel
        ) {
          return item.prediction;
        }
        return false;
      })();

      const getOrderCardType = () => {
        // 修改这里要注意的一点是 上面这里 还有另外一个  view more 的逻辑也跟着一起改
        if (isShortcutOrder) {
          return useUIOptimisationHotQuestion ? OrderCardType.newCarousel : OrderCardType.smallOrder;
        } else {
          return orderCardType === OrderCardType.oldCarousel ? OrderCardType.oldCarousel : OrderCardType.newCarousel;
        }
      };

      return {
        ...item,
        image: cdnFile(item.image),
        price: formatPrice(item.price),
        orderId: item.order_id,
        itemCount: item.total_item_count,
        orderStatusText: item.status,
        totalPrice: formatPrice(item.total_item_price),
        showPredictedText: item.style === OrderSelectionRanking.TopTwoOrders || showPredictedLabel,
        predictedText: getTrans('order_picks_for_you'),
        action: {
          info: {
            ...item,
            image: cdnFile(item.image),
            price: formatPrice(item.price),
            itemsText: getTrans(ITEMS_TEXT),
            itemText: getTrans(ITEM_TEXT),
            totalText: getTrans(TOTAL_TEXT),
            totalPrice: formatPrice(item.total_item_price),
            orderStatusText: item.status,
            orderId: item.order_id,
            orderSn: item.order_sn,
            returnSn: item.return_sn,
            returnId: item.return_id,
            itemCount: item.total_item_count,
            // 回显的Order只有新旧两种样式 + shortcutOrder small order样式
            // orderCardType: isShortcutOrder
            //   ? OrderCardType.smallOrder
            //   : orderCardType === OrderCardType.oldCarousel
            //     ? OrderCardType.oldCarousel
            //     : OrderCardType.newCarousel,
            orderCardType: getOrderCardType(),
            handleClick: useForbidOrderRedirect(isSeller) ? undefined : () => handleOrderRedirect(item),
          },
          func: 'showOrder',
          nextParams: {
            ...nextParams,
            message_text: item.title,
            message_extra: {
              node_id,
              params: params || {},
            },
            triggered_option: isForRR
              ? undefined
              : {
                triggered_type: RuleBasedMatchOptionTriggeredType.OrderOrTransactionType,
                triggered_index: index,
                round_id: roundId,
                dialogue_id: dialogueId,
                reply_type: REPLY_TYPE.ORDER_LIST,
              },
          },
        },
      };
    });

    const handleRequestFaq = () => {
      const { dialogueId, faq_node_id, isHistory, ext_key_value } = props;

      // 第一次点击发送请求 FAQ
      if (!isFirstClose) {
        return;
      }

      if (isHistory) {
        handleClickHistoryButton();
        return;
      }

      handleRequest('postMessage', {
        session_id: sessionId,
        dialogue_id: dialogueId,
        input_type: INPUT_TYPE.BUTTON,
        message_text: '',
        message_extra: {
          node_id: faq_node_id,
          ext_key_value,
          params: { order_id: 0 },
        },
        ...customCallback,
      });
      setIsFirstClose(false);
    };

    const onClosePopup = () => {
      if (props.has_faq) {
        handleRequestFaq();
      }

      startDetectInteraction();
    };

    const tabs = isSeller ? sellerTitles : buyerTitles;
    let moreOrderText = MORE_ORDER_TEXT;
    let selectOrderText = SELECT_ORDER_TEXT;

    if (isForRR) {
      moreOrderText = MORE_RR_REQUEST_TEXT;
      selectOrderText = SELECT_RR_REQUEST_TEXT;
    }

    if (orderCardType === OrderCardType.verticalList) {
      moreOrderText = VIEW_ALL_TEXT;
      selectOrderText = SELECT_TEXT;
    } else if (orderCardType === OrderCardType.twoRowCarousel) {
      moreOrderText = VIEW_ALL_ORDERS_TEXT;
      selectOrderText = SELECT_TEXT;
    }

    return {
      ...trackingProps,
      ...props,
      content,
      tabs,
      onLoadMore,
      itemsText: getTrans(ITEMS_TEXT),
      itemText: getTrans(ITEM_TEXT),
      totalText: getTrans(TOTAL_TEXT),
      loadMoreOrderText: moreButtonText || getTrans(moreOrderText),
      selectOrderText: selectOrderButtonText || getTrans(selectOrderText),
      panelTitleText: isSeller ? getTrans(SELLER_ORDER_LIST_TITLE_TEXT) : getTrans(BUYER_ORDER_LIST_TITLE_TEXT),
      showOrderId: isSeller ? true : false,
      defaultSelected,
      onClosePopup,
      ...clickHandlerProps,
    };
  }
};
